<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import Button from 'dashboard/components-next/button/Button.vue';
import DialerPanel from './DialerPanel.vue';
import { useAciSipConnection } from 'dashboard/api/telephony_aci/useAciSipConnection';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';

const isDialerOpen = ref(false);

// Get SIP connection state
const {
  isConnected,
  isConnecting,
  connectionError,
  callState,
  callDirection,
  callNumber,
  remoteDisplayName,
  connect,
} = useAciSipConnection();

const currentAccountId = useMapGetter('getCurrentAccountId');
const currentUser = useMapGetter('getCurrentUser');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const isTelephonyEnabled = computed(() => {
  return isFeatureEnabledonAccount.value(
    currentAccountId.value,
    FEATURE_FLAGS.ASTERISK_TELEPHONY
  );
});

// Agente sem sip_extension cadastrado: o GET /asterisk/my_extension retorna
// 404 NO_EXTENSION_ASSIGNED. Sem isso o launcher tentava conectar SIP em
// todo onMounted, falhava e poluia console + setava connectionError visivel
// pra usuarios que nao deveriam ver o feature.
const hasSipExtension = computed(() => !!currentUser.value?.sip_extension);

const canUseDialer = computed(
  () => isTelephonyEnabled.value && hasSipExtension.value
);

onMounted(async () => {
  if (!canUseDialer.value) return;

  if (callState.value === 'ringing' && callDirection.value === 'incoming') {
    isDialerOpen.value = true;
  }

  if (isConnected.value || isConnecting.value) return;

  await connect();
});

// Conecta quando o ramal e cadastrado/linkado depois do mount (sem F5).
// O backend broadcasta `agent.updated` apos CreateExtensionJob, atualizando
// currentUser.sip_extension -> hasSipExtension. Sem `immediate`: o onMounted
// ja cobre a carga inicial. Guard isConnected/isConnecting evita dupla conexao.
watch(hasSipExtension, has => {
  if (has && !isConnected.value && !isConnecting.value) {
    connect();
  }
});

// Auto-open dialer when incoming call is detected
watch(
  [callState, callDirection],
  ([newCallState, newCallDirection]) => {
    if (newCallState === 'ringing' && newCallDirection === 'incoming') {
      isDialerOpen.value = true;
    }
  },
  { immediate: true }
);

const showDialerLauncher = computed(() => canUseDialer.value);

const toggleDialer = () => {
  isDialerOpen.value = !isDialerOpen.value;
};

const closeDialer = () => {
  isDialerOpen.value = false;
};

// Abre o dialer quando solicitado por outros componentes (ex: VoiceCallButton)
const openDialer = () => {
  isDialerOpen.value = true;
};

// Handler para chamada recebida via evento de bus
// This also handles HMR edge cases where composable refs may be stale:
// AciSipClient emits the bus event directly, so even if the composable's
// onUserAgentAction callback references old refs, this handler updates the current ones.
const handleIncomingCallEvent = payload => {
  if (callState.value === 'idle' && payload?.number) {
    callState.value = 'ringing';
    callDirection.value = 'incoming';
    callNumber.value = payload.number;
    remoteDisplayName.value = payload.displayName || null;
  }
  if (!isDialerOpen.value) {
    isDialerOpen.value = true;
  }
};

// Escuta eventos para abrir o dialer
onMounted(() => {
  emitter.on(BUS_EVENTS.TELEPHONY_SHOW_DIALER, openDialer);
  emitter.on(BUS_EVENTS.TELEPHONY_INCOMING_CALL, handleIncomingCallEvent);
});

onUnmounted(() => {
  emitter.off(BUS_EVENTS.TELEPHONY_SHOW_DIALER, openDialer);
  emitter.off(BUS_EVENTS.TELEPHONY_INCOMING_CALL, handleIncomingCallEvent);
});

// Computed for incoming call state (visual feedback on button)
const isIncomingCall = computed(() => {
  return callState.value === 'ringing' && callDirection.value === 'incoming';
});

// Computed for active call state
const hasActiveCall = computed(() => {
  return ['calling', 'ringing', 'connecting', 'connected'].includes(
    callState.value
  );
});

// Status tooltip text
const statusTooltip = computed(() => {
  if (isIncomingCall.value) return 'Chamada Recebida!';
  if (connectionError.value) return connectionError.value;
  if (isConnecting.value) return 'Conectando ao SIP...';
  if (!isConnected.value) return 'SIP Desconectado';
  if (hasActiveCall.value) return 'Em chamada';
  return 'Chamada Voip Conectada';
});

// Icon color based on SIP status
const iconColorClass = computed(() => {
  // Incoming call - red with animation
  if (isIncomingCall.value) {
    return '!text-n-ruby-11 animate-bounce';
  }
  // Connection error or disconnected - red
  if (connectionError.value || (!isConnected.value && !isConnecting.value)) {
    return '!text-n-ruby-11';
  }
  // Connecting - amber with pulse
  if (isConnecting.value) {
    return '!text-n-amber-11 animate-pulse';
  }
  // Active call (not incoming) - teal with pulse
  if (hasActiveCall.value) {
    return '!text-n-teal-11 animate-pulse';
  }
  // Connected and idle - teal
  return '!text-n-teal-11';
});
</script>

<template>
  <div v-if="showDialerLauncher">
    <!-- Inline navbar button -->
    <Button
      v-tooltip="statusTooltip"
      :icon="isIncomingCall ? 'i-lucide-phone-incoming' : 'i-lucide-phone'"
      variant="ghost"
      color="slate"
      size="sm"
      class="!size-8 !rounded-lg !bg-transparent hover:!bg-n-alpha-1"
      :class="iconColorClass"
      @click="toggleDialer"
    />

    <!-- Dialer Panel (teleported to body so it stays visible on all routes) -->
    <Teleport to="body">
      <DialerPanel v-if="isDialerOpen" @close="closeDialer" />
    </Teleport>
  </div>
</template>
